import { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ForumPost } from '~/api/forum/types';
import type { EditorRefHandle } from '~/components';

import { useUpdateForumPost } from '~/api/forum/useUpdateForumPost';
import { Button, RichTextEditor } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { mergeRefs } from '~/utils/mergeRefs';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type { EditForumPostForm as TEditForumPostForm } from './EditForumPostForm.types';

import { buttonsStyle, formStyle } from './EditForumPostForm.css';

type Props = {
  forumPost: ForumPost;
  onFinish: () => void;
};

export const EditForumPostForm = ({ forumPost, onFinish }: Props) => {
  const mutation = useUpdateForumPost(forumPost.id);

  const { control, handleSubmit } = useForm<TEditForumPostForm>({
    defaultValues: {
      title: forumPost.title,
      content: forumPost.content,
    },
  });

  const editorRef = useRef<EditorRefHandle>(null);

  const { t } = useTranslation(['common', 'forum', 'validation']);

  const onSubmit = handleSubmit((data) => {
    // Manual dirty check because RHF `formState` is not reliable
    const isDirty = data.title !== forumPost.title || JSON.stringify(data.content) !== JSON.stringify(forumPost.content);

    if (!isDirty) return onFinish();

    mutation.mutate(
      { title: data.title, content: JSON.stringify(data.content) },
      {
        onSuccess: () => {
          toast.success({ description: t('forum:alerts.editPost.success') });
          onFinish();
        },
        onError: () => toast.danger({ description: t('forum:alerts.editPost.error') }),
      },
    );
  });

  return (
    <Form className={formStyle} onSubmit={onSubmit}>
      <Controller
        control={control}
        name="title"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('forum:createPost.fields.title.label')}</Field.Label>
            <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />
            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{
          required: { value: true, message: t('validation:required') },
          validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
          maxLength: { value: 200, message: t('validation:maxLength', { attribute: t('forum:createPost.fields.title.label'), max: 200 }) },
        }}
      />

      <Controller
        control={control}
        name="content"
        render={({ field: { name, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('forum:createPost.fields.content.label')}</Field.Label>
            <RichTextEditor isInvalid={invalid} mode="json" onChange={onChange} ref={mergeRefs(editorRef, ref)} value={value} />
            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{ validate: { notEmpty: () => (editorRef.current?.isEmpty() ? t('validation:required') : true) } }}
      />

      <div className={buttonsStyle}>
        <Button disabled={mutation.isPending} emphasis="secondary" onClick={() => onFinish()} type="button">
          {t('common:actions.cancel')}
        </Button>

        <Button icon="Save" loading={mutation.isPending} type="submit">
          {t('common:actions.save')}
        </Button>
      </div>
    </Form>
  );
};
